import ks from "../ReUsableTS/kBrowserShared";
import { kUIText } from "./kUIText";
import { kNewChoiceCollector } from "./kEditableColumn";
import { AsKTimeOrNull, kEdTblSelectionMode, kHTMLEditableColType, kNaturalStringCompare } from "./kEditableTableTypes";
import type { kRichHtmlCellEditorHost } from "./kEditableTableTypes";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kTime } from "../ReUsableTS/kBrowserObjects";

// Member bodies of kEditableTableCell moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTableCell member keeps its signature and delegates here.
export class kEditableTableCellEdit {
    public static ColorChange(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ColorPicker){
                Self.SetNewValue(Self.ColorPicker.value);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.ColorChange");
            return;
        }
    }

    // Picked the "add new item" entry in a Select dropdown: tear the inline
    // <select> down (so a stray blur can't commit the sentinel), repaint the
    // current value in read mode, then collect the new name. The committed name
    // arrives at _OnNewSelectChoice.
    public static _StartAddNewChoice(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_AddingNewChoice"] = true;
            if (Self.ChoiceDD && Self.ChoiceDD.Select) {
                Self.ChoiceDD.Select.remove();
                Self.ChoiceDD = null;
            }
            // This path returns the cell to read mode without going through
            // SetCellReadMode, so lift the _EditSelect width/height pin here too.
            Self.Cell.style.minWidth = "";
            Self.Cell.style.minHeight = "";
            Self.Cell.textContent = ks.Nz(Self.CurrentValue);
            const Collector: kNewChoiceCollector = new kNewChoiceCollector();
            Collector.Collect(Self.ColDef, Self["_OnNewSelectChoice"]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._StartAddNewChoice");
        }
    }

    public static _OnNewSelectChoice(Self: kEditableTableCell, NewItem: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_AddingNewChoice"] = false;
            const Name: string = ks.Nz(NewItem);
            if (Name === "") { return; } // cancelled — cell already shows the old value
            if (Self.ColDef.Options.indexOf(Name) === -1) {
                Self.ColDef.Options.push(Name);
                // Keep the choices alphabetical so the new item lands in order the
                // next time the dropdown (or the row-form select) is built.
                Self.ColDef.Options.sort(kNaturalStringCompare);
            }
            Self.SetNewValue(Name, true);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._OnNewSelectChoice");
        }
    }

    public static DateInputChanged(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.DatePicker){
                Self.SetNewValue(Self.DatePicker.ChosenDate);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.DateInputChanged");
            return;
        }
    }

    public static SetCellReadMode(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // Release the edit-time width/height pin (see _MountInlineEditor).
            if (Self.Cell !== null) { Self.Cell.style.minWidth = ""; Self.Cell.style.minHeight = ""; }
            if (Self.ColDef && Self.ParentTable) {
                // Columns with a CustomEditor (people picker, link cell, …)
                // commit through their own editor popup flow — the popup
                // calls Cell.SetNewValue(structuredPayload) when the user
                // hits OK. The generic _Complete*Edit branches below would
                // then re-read cell.textContent / the inline input and
                // SetNewValue(flatString) over that structured value,
                // clobbering it with a display-name string the column's
                // CustomRenderer doesn't know how to re-hydrate. Skip the
                // generic finishers entirely for those columns.
                if (Self.ColDef.CustomEditor !== null) {
                    return;
                }
                switch (Self.ColDef.Type) {
                    case kHTMLEditableColType.Select:      Self["_CompleteSelectEdit"]();    break;
                    case kHTMLEditableColType.Color:       Self["_CompleteColorEdit"]();     break;
                    case kHTMLEditableColType.SelectMulti: Self["_CompleteMultiSelectEdit"](); break;
                    case kHTMLEditableColType.Date:        Self["_CompleteDateEdit"]();      break;
                    case kHTMLEditableColType.RichHtml:    Self["_CompleteRichHtmlEdit"]();  break;
                    case kHTMLEditableColType.Text:        Self["_CompleteTextEdit"]();      break;
                    case kHTMLEditableColType.Currency:    Self["_CompleteCurrencyEdit"]();  break;
                    case kHTMLEditableColType.Number:      Self["_CompleteNumberEdit"]();    break;
                    case kHTMLEditableColType.Checkbox:
                    case kHTMLEditableColType.Radio:
                        break; // Always in read mode — nothing to do
                    default:
                        break;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SetCellReadMode");
            return;
        }
    }

    public static _CompleteColorEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ColorPicker !== null) {
                Self.SetNewValue(Self.ColorPicker.value, true);
                Self.ColorPicker.remove();
                Self.ColorPicker = null;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteColorEdit");
        }
    }

    public static _CompleteMultiSelectEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ChoiceMultiDD && Self.ChoiceMultiDD.OuterDiv) {
                Self.SetNewValue(Self.ChoiceMultiDD.SelectedChoices().join("; "));
                Self.ChoiceMultiDD.OuterDiv.remove();
                Self.ChoiceMultiDD = null;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteMultiSelectEdit");
        }
    }

    // "+ Add new" clicked in a SelectMulti popup: collect the name (consumer's
    // OnNewItem, else the built-in dialog), then hand it to _OnNewMultiChoice.
    public static _RequestNewMultiChoice(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Collector: kNewChoiceCollector = new kNewChoiceCollector();
            Collector.Collect(Self.ColDef, Self["_OnNewMultiChoice"]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._RequestNewMultiChoice");
        }
    }

    public static _OnNewMultiChoice(Self: kEditableTableCell, NewItem: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Name: string = ks.Nz(NewItem);
            if (Name === "") { return; } // cancelled
            // Persist plain-string Options here. For a MultiSelectOptions-based
            // column the live control's ChoiceOptions IS this.ColDef.MultiSelectOptions
            // (same array), so AddAndSelectChoice's push already persists the RENDERED
            // option — pushing a bare (un-rendered) duplicate here would shadow it and
            // make it unselectable, so we deliberately do NOT.
            if (Self.ColDef.MultiSelectOptions.length === 0
                && Self.ColDef.Options.indexOf(Name) === -1) {
                Self.ColDef.Options.push(Name);
                Self.ColDef.Options.sort(kNaturalStringCompare);
            }
            if (Self.ChoiceMultiDD !== null) {
                Self.ChoiceMultiDD.AddAndSelectChoice(Name);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._OnNewMultiChoice");
        }
    }

    public static _CompleteRichHtmlEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.HtmlPopup !== null) {
                const Popup: kRichHtmlCellEditorHost = Self.HtmlPopup;
                Self.HtmlPopup = null; // Null first so the popup's Close cannot re-enter this path
                Popup.Commit();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteRichHtmlEdit");
        }
    }

    public static _CompleteDateEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.DatePicker && Self.DatePicker.Input) {
                Self.SetNewValue(Self.DatePicker.ChosenDate);
                if (Self.CurrentValue && Self.CurrentValue.Year > 1970) {
                    Self.Cell.textContent = Self.CurrentValue.ToLocalShortDate();
                } else {
                    Self.Cell.textContent = "";
                }
                const CurrDate: kTime | null = AsKTimeOrNull(Self.CurrentValue);
                const OrgDate: kTime | null = AsKTimeOrNull(Self.OrgValue);
                if ((CurrDate === null) !== (OrgDate === null)) {
                    Self.SetDirty();
                } else if (CurrDate !== null && OrgDate !== null) {
                    if (CurrDate.ToISODate() !== OrgDate.ToISODate()) {
                        Self.SetDirty();
                    } else {
                        Self.SetClean();
                    }
                } else {
                    Self.SetClean();
                }
                Self.DatePicker.Input.remove();
                Self.DatePicker = null;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteDateEdit");
        }
    }

    /** Moved from kEditableTableCell._CompleteInlineEdit (size split); the class member delegates here. */
    public static _CompleteInlineEdit(Self: kEditableTableCell, Parse: () => any, Numeric: boolean, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.contentEditable = "false";
            if (Numeric) { Self.Cell.style.textAlign = "right"; }
            const Value: any = Parse();
            if (Value !== Self.OrgValue) {
                if (Numeric) { Self.SetNewValue(Value); Self.SetDirty(); }
                else { Self.SetNewValue(Value, true); }
                if (Self.ParentTable && Self.RowIndex === Self.ParentTable.Rows.Items.length - 1) {
                    if (ks.Nz(Self.Cell.textContent) !== "") { Self.ParentTable.AddEmptyRows(1); }
                }
            } else {
                Self.SetClean();
                // Unchanged: the edit was seeded with the raw number - paint the formatted value back ("12 345", "1 234,50 kr").
                if (Numeric) { Self["_PaintOrgNumeric"](Self.OrgValue); }
            }
            Self.Cell.removeEventListener("beforeinput", Self["_TextEnterPrevent"]);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteInlineEdit");
        }
    }

    public static _CompleteTextEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_CompleteInlineEdit"](() => Self.Cell.textContent, false, "kEditableTableCell._CompleteTextEdit");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteTextEdit");
        }
    }

    // Escape while inline-editing a Text/Number/Currency cell: abandon the typed
    // edit and restore the cell's committed value (Excel's Esc-cancels-edit). The
    // typed text was never committed (commit happens on blur / Tab / Enter), so we
    // only repaint OrgValue, drop back to read mode and stay clean + selected.
    public static CancelTextEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.contentEditable = "false";
            Self.Cell.removeEventListener("beforeinput", Self["_TextEnterPrevent"]);
            switch (Self.ColDef.Type) {
                case kHTMLEditableColType.Currency:
                    Self.Cell.textContent = ks.LocalCurrencyNumber(ks.N0(Self.OrgValue));
                    Self.Cell.style.textAlign = "right";
                    break;
                case kHTMLEditableColType.Number:
                    Self.Cell.textContent = kUIText.Count(ks.N0(Self.OrgValue), 6);
                    Self.Cell.style.textAlign = "right";
                    break;
                default:
                    Self.Cell.textContent = ks.Nz(Self.OrgValue);
                    break;
            }
            Self.CurrentValue = Self.OrgValue;
            Self.SetClean();
            if (Self.ParentTable !== null
                && (Self.ColDef.Type === kHTMLEditableColType.Number
                    || Self.ColDef.Type === kHTMLEditableColType.Currency)) {
                Self.ParentTable.ApplyColumnDataBars(Self.ColumnIndex);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.CancelTextEdit");
        }
    }

    public static _CompleteCurrencyEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_CompleteInlineEdit"](() => ks.NumberFromCurrency(ks.Nz(Self.Cell.textContent)), true, "kEditableTableCell._CompleteCurrencyEdit");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteCurrencyEdit");
        }
    }

    public static _CompleteNumberEdit(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_CompleteInlineEdit"](() => Number(Self.Cell.textContent), true, "kEditableTableCell._CompleteNumberEdit");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._CompleteNumberEdit");
        }
    }

    /** Moved from kEditableTableCell._PaintOrgValue (size split); the class member delegates here. */
    public static _PaintOrgValue(Self: kEditableTableCell, Value:any): void {
        if (ks.KErrorMode === true) { return; }
        try {
            switch (Self.ColDef.Type) {
                case kHTMLEditableColType.Date: Self["_PaintOrgDate"](Value); break;
                case kHTMLEditableColType.Currency:
                case kHTMLEditableColType.Number: Self["_PaintOrgNumeric"](Value); break;
                case kHTMLEditableColType.Color: Self._PaintColorSwatch(ks.Nz(Value)); break;
                case kHTMLEditableColType.Checkbox:
                    Self.Cell.innerHTML = Self._CheckboxHtml(!!Value);
                    Self.Cell.style.textAlign = "center";
                    break;
                case kHTMLEditableColType.Radio:
                    Self.Cell.innerHTML = Self._RadioHtml(!!Value);
                    Self.Cell.style.textAlign = "center";
                    break;
                case kHTMLEditableColType.RichHtml: Self["_PaintRichHtmlPreview"](ks.Nz(Value)); break;
                case kHTMLEditableColType.Select:
                    Self._PaintSelectChip(ks.Nz(Value));
                    Self["_ApplyOptionTooltip"](ks.Nz(Value));
                    break;
                case kHTMLEditableColType.SelectMulti: Self._PaintMultiChips(ks.Nz(Value)); break;
                default: Self.Cell.textContent = ks.Nz(Value); break;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._PaintOrgValue");
        }
    }

    /** Moved from kEditableTableCell._PaintOrgDate (size split); the class member delegates here. */
    public static _PaintOrgDate(Self: kEditableTableCell, Value:any): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(!Value){ return; }
            Self.Cell.textContent = Value.ToLocalShortDate();
            Self._ApplyDateRelativeTooltip(Value);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._PaintOrgDate");
        }
    }

    /** Moved from kEditableTableCell._PaintOrgNumeric (size split); the class member delegates here. */
    public static _PaintOrgNumeric(Self: kEditableTableCell, Value:any): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(!Value){ return; }
            if(Self.ColDef.Type === kHTMLEditableColType.Currency){
                Self.Cell.textContent = ks.LocalCurrencyNumber(ks.N0(Value));
            }else{
                Self.Cell.textContent = kUIText.Count(ks.N0(Value), 6);
            }
            Self.Cell.style.textAlign = "right";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit._PaintOrgNumeric");
        }
    }

    public static SelectNextDown(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["SelectNextTo"](Self.RowIndex + 1, Self.ColumnIndex, "kEditableTableCell.SelectNextDown");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectNextDown");
        }
    }

    public static SelectNextUp(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["SelectNextTo"](Self.RowIndex - 1, Self.ColumnIndex, "kEditableTableCell.SelectNextUp");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectNextUp");
        }
    }

    public static SelectNextRight(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["SelectNextTo"](Self.RowIndex, Self.ColumnIndex + 1, "kEditableTableCell.SelectNextRight");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectNextRight");
        }
    }

    public static SelectNextTo(Self: kEditableTableCell, RowIndex: number, ColumnIndex: number, Label: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable){
                Self.ParentTable.Selection.SelectCellByIndexes(RowIndex,ColumnIndex);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectNextTo");
            return;
        }
    }

    public static SelectNextLeft(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["SelectNextTo"](Self.RowIndex, Self.ColumnIndex - 1, "kEditableTableCell.SelectNextLeft");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectNextLeft");
        }
    }

    public static SelectNextRowFirstCell(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["SelectNextTo"](Self.RowIndex + 1, 0, "kEditableTableCell.SelectNextRowFirstCell");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectNextRowFirstCell");
        }
    }

    public static SelectPrevRowLastCell(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ParentTable) {
                Self["SelectNextTo"](Self.RowIndex - 1, Self.ParentTable.Columns.Items.length - 1, "kEditableTableCell.SelectPrevRowLastCell");
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SelectPrevRowLastCell");
            return;
        }
    }

    public static SetSelectionMode(Self: kEditableTableCell, NewMode:kEdTblSelectionMode): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.Cell){
                switch (NewMode){
                    case kEdTblSelectionMode.SingleCell:
                        Self.Cell.classList.add("SelectedCellSingle");
                        Self.Cell.classList.remove("SelectedCellMulti");
                        break;
                    case kEdTblSelectionMode.NoSelection:
                        Self.Cell.classList.remove("SelectedCellSingle");
                        Self.Cell.classList.remove("SelectedCellMulti");
                        break;
                    default:
                        Self.Cell.classList.add("SelectedCellMulti");
                        break
                }
            }
            Self.SelectionMode = NewMode;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.SetSelectionMode");
            return;
        }
    }

    public static Select(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.Cell && Self.ParentTable){
                switch(Self.ColDef.Type){
                    case kHTMLEditableColType.Button:
                    case kHTMLEditableColType.Checkbox:
                    case kHTMLEditableColType.Radio:
                    // RichHtml: arrow-key navigation must not pop the editor on
                    // every traversed cell — select only; F2/space/click opens.
                    case kHTMLEditableColType.RichHtml:
                        Self.ParentTable.Selection.SetSingleCell(Self);
                        return;
                    default:
                        Self.CellClick();
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellEdit.Select");
            return;
        }
    }
}
